import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowUpRight,
  ChevronLeft,
  ExternalLink,
  Info,
  Landmark,
  Search,
  Scale,
} from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

export default function FeeWaivers() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/fee-waivers");
        setEntries(r.data);
      } catch (e) {
        toast.error("Could not load fee waivers");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const { federal, states } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filter = (e) =>
      !q ||
      e.jurisdiction_name.toLowerCase().includes(q) ||
      e.code.toLowerCase().includes(q) ||
      (e.agency || "").toLowerCase().includes(q) ||
      (e.program_name || "").toLowerCase().includes(q);
    return {
      federal: entries.filter((e) => e.scope === "federal").filter(filter),
      states: entries.filter((e) => e.scope !== "federal").filter(filter),
    };
  }, [entries, query]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[1300px] mx-auto px-6 py-10 md:py-14 flex-1 w-full" data-testid="fee-waivers-page">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/resources"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4"
            data-testid="back-to-resources"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Resources
          </Link>
        </div>
        <header className="mb-10 max-w-3xl">
          <div className="label-tiny mb-3">Directory</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-slate-900 leading-tight">
            <Scale className="inline-block w-9 h-9 mr-2 -mt-2 text-slate-700" strokeWidth={1.75} />
            Court Fee Waivers
          </h1>
          <p className="mt-4 text-slate-600">
            If you cannot afford court filing fees, most federal and state courts let you ask
            the judge to waive or defer them. Below are official government pages that
            describe the process for the federal courts and every U.S. state and D.C.
          </p>
        </header>

        {/* Notice */}
        <div
          className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3 mb-8"
          data-testid="fee-waivers-notice"
        >
          <Info className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div>
            ProSe.Claims provides links and organizational assistance only. Eligibility rules,
            forms, and fees change over time. Confirm current requirements with the official
            court, agency, or a licensed attorney before filing.
          </div>
        </div>

        {/* Search */}
        <div className="border border-slate-200 p-6 mb-10" data-testid="fee-waivers-search-card">
          <Label className="label-tiny mb-2 block">Find your jurisdiction</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by state, code, agency, or program name"
              className={`${inputCls} pl-9`}
              data-testid="fee-waivers-search-input"
            />
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {loading
              ? "Loading…"
              : `${federal.length + states.length} of ${entries.length} entries`}
          </div>
        </div>

        {/* Federal */}
        {federal.length > 0 && (
          <section className="mb-14" data-testid="fee-waivers-federal">
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="w-5 h-5 text-slate-700" />
              <h2 className="font-serif-display text-2xl text-slate-900">Federal courts</h2>
            </div>
            <div className="grid grid-cols-1">
              {federal.map((e) => (
                <FeeWaiverCard key={e.code} entry={e} />
              ))}
            </div>
          </section>
        )}

        {/* States + DC */}
        <section data-testid="fee-waivers-states">
          <h2 className="font-serif-display text-2xl text-slate-900 mb-4">States and D.C.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {states.map((e) => (
              <FeeWaiverCard key={e.code} entry={e} />
            ))}
          </div>
          {!loading && federal.length + states.length === 0 && (
            <div className="border border-slate-200 p-10 text-center text-slate-500" data-testid="fee-waivers-empty">
              No entries match your search.
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function FeeWaiverCard({ entry: e }) {
  return (
    <div
      className="group border border-slate-200 -mt-px -ml-px bg-white p-5 md:p-6 transition-shadow duration-200 hover:z-10 hover:shadow-[0_6px_18px_-12px_rgba(15,23,42,0.15)]"
      data-testid={`fee-waiver-card-${e.code}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">
            {e.code}
          </div>
          <div className="font-serif-display text-xl text-slate-900 leading-tight mt-1">
            {e.jurisdiction_name}
          </div>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="text-slate-800">
          <span className="label-tiny">Program</span>
          <div className="font-medium text-slate-900">{e.program_name}</div>
        </div>
        {e.form_number && (
          <div className="text-slate-700">
            <span className="label-tiny">Form</span>
            <div className="font-mono-tech text-xs text-slate-800">{e.form_number}</div>
          </div>
        )}
        <div className="text-slate-700">
          <span className="label-tiny">Agency</span>
          <div className="text-xs">{e.agency}</div>
        </div>
        <p className="text-sm text-slate-700 leading-relaxed pt-1">{e.plain_english}</p>
        {e.who_qualifies && (
          <div className="pt-1">
            <Badge variant="outline" className="rounded-none border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]">
              {e.who_qualifies}
            </Badge>
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <a
          href={e.official_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5"
          data-testid={`open-fee-waiver-${e.code}`}
        >
          Official page <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
        <a
          href={e.agency_homepage}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 ml-auto"
          data-testid={`open-agency-${e.code}`}
        >
          Agency <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="text-[11px] text-slate-500 mt-3">
        Last verified: <span className="tabular-nums">{e.last_verified_at || "—"}</span>
      </div>
    </div>
  );
}
